import { Exceptions } from "@akina/infra";
import type { useToasts } from "@akina/presentation";
import type { QueryObserverOptions, QueryOptions } from "@tanstack/react-query";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

const STALE_TIME = 2 * 60 * 1000;
const CACHE_TIME = 15 * 60 * 1000;

const queryDefaultOptions: () => QueryOptions & {
  useErrorBoundary: (error: { message: string; stack: string; response?: Response }) => boolean;
} = () => ({
  retry: (
    failureCount: number,
    error: {
      message: string;
      response?: Response;
      stack?: string;
    },
  ) => {
    // By default, retry only on error 500. All other errors should be properly handled.
    if (error.response?.status === 500 && failureCount < 3) return true;
    return false;
  },
  staleTime: STALE_TIME,
  cacheTime: CACHE_TIME,
  useErrorBoundary: (error: { message: string; stack?: string; response?: Response }) => {
    if (error instanceof Exceptions.Exception && error.useErrorBoundary) return true;
    return false;
  },
});

const onError =
  (addToast: ReturnType<typeof useToasts>["addToast"]) =>
  (
    error: {
      message: string;
      stack: string;
      response?: Response;
    },
    queryOptions?: QueryObserverOptions,
  ) => {
    if (queryOptions && !queryOptions.throwOnError) return;

    const toastErrors = [400, 401, 404, 500];
    if (toastErrors.includes(error.response?.status ?? 0))
      addToast({
        variant: "error",
        title: `There was an error. (${error instanceof Error ? error.name : ""})`,
        description: "It looks like we are having some internal issues. Our Team has been notified.",
      });
  };

type DeveloperToolsStore = { state?: { logHttpCalls?: boolean } };
const safeGetLocalStorageItem = (key: string): string | null => {
  const w = globalThis.window;
  if (!w) return null;
  try {
    return w.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const devToolsStore = safeGetLocalStorageItem("developer-tools-store");
const logHttpCalls = (() => {
  if (!devToolsStore) return false;
  try {
    return (JSON.parse(devToolsStore) as DeveloperToolsStore)?.state?.logHttpCalls ?? false;
  } catch {
    return false;
  }
})();

export const getQueryClient = (params: {
  addToast: ReturnType<typeof useToasts>["addToast"];
  log: (...args: unknown[]) => void;
}) =>
  new QueryClient({
    defaultOptions: {
      queries: queryDefaultOptions(),
      mutations: queryDefaultOptions(),
    },
    queryCache: new QueryCache({
      onError: (error, query) =>
        onError(params.addToast)(
          error as {
            message: string;
            stack: string;
            response?: Response;
          },
          query.options as QueryObserverOptions,
        ),
      onSettled: (data, _error, query) => {
        if (!_error && logHttpCalls) {
          params.log("[QUERY]", `[${query.queryKey.join(", ")}]`, data);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        onError(params.addToast)(
          error as {
            message: string;
            stack: string;
            response?: Response;
          },
          mutation.options as QueryObserverOptions,
        ),
      onSettled: (data, error, _variables, _context, mutation) => {
        if (!error) {
          params.log(
            "[MUTATION]",
            `[${mutation.options.mutationKey?.join(", ") ?? mutation.options.mutationFn?.toString()}]`,
            data,
          );
        }
      },
    }),
  });
